import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ChevronRight, Grip } from "lucide-react";
import { imgProps, srcSetFor, SIZES } from "@/lib/images";

export const EASE = [0.2, 0.8, 0.2, 1];

/** Page container – 1280px with generous side padding */
export const Container = ({ children, className = "", ...rest }) => (
  <div className={`mx-auto w-full max-w-site px-5 sm:px-8 lg:px-10 ${className}`} {...rest}>{children}</div>
);

/** Wider frame for hero photos and photo walls – edges breathe, content stays at 1280 */
export const Wide = ({ children, className = "", ...rest }) => (
  <div className={`mx-auto w-full max-w-[1600px] px-3 sm:px-5 lg:px-6 ${className}`} {...rest}>{children}</div>
);

/** Calm scroll reveal */
export const Reveal = ({ children, delay = 0, y = 14, className = "", as = "div", once = true, ...rest }) => {
  const reduce = useReducedMotion();
  const Comp = motion[as] || motion.div;
  return (
    <Comp
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-40px" }}
      transition={{ duration: 0.65, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </Comp>
  );
};

/** Section heading: title left, optional text-link right */
export const SectionTitle = ({ title, sub, action, to, onAction, className = "", testid, as = "h2", size = "md", index, eyebrow }) => {
  const H = as;
  const sizes = {
    sm: "text-xl sm:text-2xl",
    md: "text-[1.75rem] sm:text-[2.125rem]",
    lg: "text-[2.125rem] sm:text-[2.75rem] lg:text-[3.25rem]",
  };
  const linkCls = "inline-flex shrink-0 items-center gap-1 text-[0.9375rem] font-semibold text-foreground underline decoration-border underline-offset-[6px] transition-colors hover:decoration-foreground";
  return (
    <div className={`flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-8 ${className}`} data-testid={testid}>
      <div className="max-w-2xl">
        {(index || eyebrow) && (
          <p className="mb-4 flex items-center gap-3 text-xs font-medium tracking-wide text-muted-foreground" data-testid={testid ? `${testid}-eyebrow` : undefined}>
            {index && <span className="tabular text-foreground">{index}</span>}
            <span className="h-px w-8 bg-border" aria-hidden />
            {eyebrow && <span>{eyebrow}</span>}
          </p>
        )}
        <H className={`${sizes[size] || sizes.md} leading-[1.02] tracking-[-0.03em] text-foreground`}>{title}</H>
        {sub && <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">{sub}</p>}
      </div>
      {action && (to || onAction) && (
        to ? (
          <Link to={to} className={linkCls} data-testid={testid ? `${testid}-action` : undefined}>{action}</Link>
        ) : (
          <button type="button" onClick={onAction} className={linkCls} data-testid={testid ? `${testid}-action` : undefined}>{action}</button>
        )
      )}
    </div>
  );
};

/** Page intro: h1 + lead paragraph, left aligned (content pages) */
export const PageIntro = ({ title, lead, kicker, children, className = "", testid, back }) => (
  <Container className={`pt-8 sm:pt-12 lg:pt-16 ${className}`} data-testid={testid}>
    {back && (
      <Link to={back.to} className="mb-5 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground" data-testid={back.testid}>
        <ChevronRight className="h-4 w-4 rotate-180" /> {back.label}
      </Link>
    )}
    {kicker && <p className="mb-3 text-sm font-medium text-muted-foreground">{kicker}</p>}
    <h1 className="max-w-3xl text-[2.5rem] leading-[0.98] tracking-[-0.035em] text-foreground sm:text-[3.25rem] lg:text-[4rem]">{title}</h1>
    {lead && <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl">{lead}</p>}
    {children}
  </Container>
);

/** Shared "photo comes alive" reveal: settles from a slight zoom as it enters the viewport */
const REVEAL = {
  initial: { opacity: 0, scale: 1.08 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true, margin: "0px 0px -8% 0px" },
  transition: { duration: 1.1, ease: EASE },
};

/**
 * Photo with rounded corners + responsive webp srcset.
 * The reveal animates a wrapper (not the <img>) so the CSS hover zoom in `.img-zoom img` keeps working.
 */
export const Img = ({ src, alt = "", className = "", imgClassName = "", eager = false, radius = "rounded-2xl", position, sizes = SIZES.third, reveal = true, children, ...rest }) => {
  const reduce = useReducedMotion();
  const live = reveal && !reduce;
  return (
    <div className={`overflow-hidden bg-muted ${className.includes("absolute") ? "" : "relative"} ${radius} ${className}`}>
      <motion.div className="h-full w-full" {...(live ? REVEAL : {})}>
        <img
          {...imgProps(src, sizes)}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          fetchPriority={eager ? "high" : undefined}
          decoding="async"
          style={position ? { objectPosition: position } : undefined}
          className={`h-full w-full object-cover ${imgClassName}`}
          {...rest}
        />
      </motion.div>
      {children}
    </div>
  );
};

/** Wide photo banner inside the frame (content pages) */
export const PhotoBanner = ({ src, alt = "", position = "50% 50%", className = "", testid, children, aspect = "aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9]" }) => (
  <Wide className={`mt-8 ${className}`} data-testid={testid}>
    <div className={`relative overflow-hidden rounded-[22px] bg-muted sm:rounded-[28px] ${aspect}`}>
      <img {...imgProps(src, SIZES.wide)} alt={alt} className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: position }} fetchPriority="high" decoding="async" />
      {children}
    </div>
  </Wide>
);

/**
 * Full-bleed photo hero for content pages. Sits under the transparent header (SiteLayout sets
 * `overlay`), copy bottom-left over a scrim, slow settle-zoom on load, parallax + copy fade on scroll.
 */
export const PhotoHero = ({ title, lead, kicker, image, mobileImage, alt = "", position = "50% 50%", mobilePosition, children, testid, className = "", height = "h-[66svh] min-h-[480px] max-h-[760px]", caption }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "14%"]);
  const o = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const up = (delay) => ({ initial: reduce ? false : { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay, ease: EASE } });
  const posStyle = mobilePosition ? { "--pos-m": mobilePosition, "--pos": position } : { objectPosition: position };
  const posCls = mobilePosition ? "[object-position:var(--pos-m)] md:[object-position:var(--pos)]" : "";
  return (
    <section ref={ref} className={`relative overflow-hidden bg-[hsl(214,18%,16%)] ${height} ${className}`} data-testid={testid}>
      <motion.div style={{ y }} className="absolute inset-x-0 -top-[8%] h-[116%] will-change-transform">
        <motion.div initial={reduce ? false : { scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 6.5, ease: [0.16, 1, 0.3, 1] }} className="h-full w-full origin-center">
          <picture className="block h-full w-full">
            {mobileImage && <source media="(max-width: 767px)" type="image/webp" srcSet={srcSetFor(mobileImage)} sizes="100vw" />}
            <source type="image/webp" srcSet={srcSetFor(image)} sizes="100vw" />
            <img src={image} alt={alt} className={`h-full w-full object-cover ${posCls}`} style={posStyle} fetchPriority="high" decoding="async" />
          </picture>
        </motion.div>
      </motion.div>
      <div className="scrim-nav pointer-events-none absolute inset-x-0 top-0 h-44" aria-hidden />
      <div className="scrim-hero pointer-events-none absolute inset-0" aria-hidden />
      <div className="scrim-hero-strong pointer-events-none absolute inset-x-0 bottom-0 h-[64%] md:hidden" aria-hidden />
      <Container className="relative flex h-full flex-col justify-end pb-9 lg:pb-12">
        <motion.div style={{ opacity: o }} className="flex items-end justify-between gap-10">
          <div className="min-w-0">
            {kicker && <motion.p {...up(0.05)} className="hero-shadow text-[0.9375rem] font-semibold text-white" data-testid={testid ? `${testid}-kicker` : undefined}>{kicker}</motion.p>}
            <motion.h1 {...up(0.1)} className="hero-shadow mt-3 max-w-4xl text-[2.5rem] font-semibold leading-[0.96] tracking-[-0.04em] text-white sm:text-[3.25rem] lg:text-[4.25rem]" data-testid={testid ? `${testid}-title` : undefined}>{title}</motion.h1>
            {lead && <motion.p {...up(0.16)} className="hero-shadow mt-4 max-w-2xl text-[1.0625rem] leading-relaxed text-white/90 sm:text-lg lg:mt-5 lg:text-xl" data-testid={testid ? `${testid}-lead` : undefined}>{lead}</motion.p>}
            {children && <motion.div {...up(0.24)}>{children}</motion.div>}
          </div>
          {caption && <motion.p {...up(0.28)} className="hero-shadow hidden max-w-[30ch] shrink-0 pb-1.5 text-right text-sm leading-snug text-white/70 xl:block" data-testid={testid ? `${testid}-caption` : undefined}>{caption}</motion.p>}
        </motion.div>
      </Container>
    </section>
  );
};

/**
 * Image with gentle scroll parallax (range in % of its own height, keep <= 6) plus the shared reveal.
 * Three layers: parallax (y) -> reveal (scale/opacity) -> plain <img> so `.img-zoom img` hover still works.
 * `mobileSrc` swaps in a portrait rendition below 768px (art direction for tall phone crops).
 */
export const ParallaxImg = ({ src, mobileSrc, alt = "", className = "", position = "50% 50%", mobilePosition, range = 5, eager = false, radius = "rounded-2xl", sizes = SIZES.half, reveal = true, children }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${range}%`, `${range}%`]);
  const live = reveal && !reduce;
  const img = (
    <img
      {...imgProps(src, sizes)}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      fetchPriority={eager ? "high" : undefined}
      decoding="async"
      style={{ objectPosition: position }}
      className="h-full w-full object-cover"
    />
  );
  return (
    <div ref={ref} className={`overflow-hidden bg-muted ${className.includes("absolute") ? "" : "relative"} ${radius} ${className}`}>
      <motion.div style={{ y: reduce ? 0 : y }} className="absolute inset-x-0 -top-[7%] h-[114%]">
        <motion.div className="h-full w-full" {...(live ? REVEAL : {})}>
          {mobileSrc ? (
            <picture className="block h-full w-full">
              <source media="(max-width: 767px)" type="image/webp" srcSet={srcSetFor(mobileSrc)} sizes="100vw" />
              {mobilePosition ? (
                <img
                  {...imgProps(src, sizes)}
                  alt={alt}
                  loading={eager ? "eager" : "lazy"}
                  fetchPriority={eager ? "high" : undefined}
                  decoding="async"
                  className="h-full w-full object-cover [object-position:var(--pos-m)] md:[object-position:var(--pos)]"
                  style={{ "--pos-m": mobilePosition, "--pos": position }}
                />
              ) : img}
            </picture>
          ) : img}
        </motion.div>
      </motion.div>
      {children}
    </div>
  );
};

/**
 * Horizontal snap row of photo cards. On desktop two flat arrow buttons sit over the rail's edges
 * and fade out at either end; on touch devices the rail simply scrolls.
 */
export const HScroll = ({ items = [], testid, className = "", renderItem, arrows = true }) => {
  const ref = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setEdge({ start: el.scrollLeft <= 2, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2 });
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const ro = "ResizeObserver" in window ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => { el.removeEventListener("scroll", update); window.removeEventListener("resize", update); ro?.disconnect(); };
  }, [items.length]);
  const by = (dir) => {
    const el = ref.current;
    if (!el) return;
    const first = el.querySelector("[data-rail-item]");
    const step = first ? first.getBoundingClientRect().width + 20 : el.clientWidth * 0.5;
    el.scrollBy({ left: dir * step * 2, behavior: "smooth" });
  };
  const arrowCls = "pointer-events-auto absolute top-[36%] hidden h-11 w-11 items-center justify-center rounded-md bg-card text-foreground shadow-pop ring-hairline transition-[opacity,transform,background-color] duration-200 hover:bg-secondary active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:flex";
  return (
    <div className={`relative ${className}`}>
      <div ref={ref} className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 scroll-pl-5 sm:-mx-8 sm:px-8 sm:scroll-pl-8 lg:-mx-10 lg:gap-5 lg:px-10 lg:scroll-pl-10" data-testid={testid} data-edge-start={edge.start ? "true" : "false"} data-edge-end={edge.end ? "true" : "false"}>
        {items.map((it, i) => (
          <div key={(it.src || it.id || "") + i} data-rail-item className={`snap-start shrink-0 ${it.w || "w-[72vw] sm:w-[40vw] lg:w-[292px]"}`} data-testid={testid ? `${testid}-item-${i}` : undefined}>
            {renderItem ? renderItem(it, i) : (
              <figure>
                <Img src={it.src} alt={it.alt || it.caption || ""} className={`img-zoom ${it.aspect || "aspect-[4/5]"}`} position={it.position} sizes={SIZES.quarter} />
                {(it.caption || it.meta) && (
                  <figcaption className="mt-3">
                    <p className="text-[0.9375rem] font-semibold text-foreground">{it.caption}</p>
                    {it.meta && <p className="text-sm text-muted-foreground">{it.meta}</p>}
                  </figcaption>
                )}
              </figure>
            )}
          </div>
        ))}
      </div>
      {arrows && items.length > 1 && (
        <div className="pointer-events-none absolute inset-0 hidden lg:block" aria-hidden={edge.start && edge.end}>
          <button type="button" onClick={() => by(-1)} tabIndex={edge.start ? -1 : 0} aria-label="Previous" data-testid={testid ? `${testid}-prev` : undefined} className={`${arrowCls} -left-5 ${edge.start ? "pointer-events-none opacity-0" : "opacity-100"}`}>
            <ChevronRight className="h-5 w-5 rotate-180" />
          </button>
          <button type="button" onClick={() => by(1)} tabIndex={edge.end ? -1 : 0} aria-label="Next" data-testid={testid ? `${testid}-next` : undefined} className={`${arrowCls} -right-5 ${edge.end ? "pointer-events-none opacity-0" : "opacity-100"}`}>
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

/** Price with tabular numerals */
export const Price = ({ value, prefix, suffix, size = "md", className = "", testid }) => {
  const s = { sm: "text-base", md: "text-lg", lg: "text-2xl", xl: "text-3xl" }[size] || "text-lg";
  return (
    <span className={`inline-flex items-baseline gap-1 whitespace-nowrap ${className}`} data-testid={testid}>
      {prefix && <span className="text-sm text-muted-foreground">{prefix}</span>}
      <span className={`font-semibold tracking-tight text-foreground tnum ${s}`}>{value}</span>
      {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
    </span>
  );
};

/** Plain definition rows: [{label, value}] */
export const SpecList = ({ rows = [], testid, className = "" }) => (
  <dl className={`divide-y divide-border ${className}`} data-testid={testid}>
    {rows.filter(Boolean).map((r, i) => (
      <div key={r.label + i} className="grid grid-cols-[minmax(7rem,1fr)_2fr] gap-6 py-3.5 text-[0.9375rem]">
        <dt className="text-muted-foreground">{r.label}</dt>
        <dd className="font-medium text-foreground tabular">{r.value}</dd>
      </div>
    ))}
  </dl>
);

/** Listing photo mosaic: 1 large + 4 small on desktop, single photo on mobile */
export const PhotoMosaic = ({ images = [], alt = "", onOpen, label, testid = "photo-mosaic" }) => {
  const imgs = images.slice(0, 5);
  return (
    <div className="relative" data-testid={testid}>
      <button type="button" onClick={() => onOpen?.(0)} className="block w-full overflow-hidden rounded-[22px] bg-muted lg:hidden" aria-label={label} data-testid={`${testid}-mobile`}>
        <img {...imgProps(imgs[0], SIZES.full)} alt={alt} className="aspect-[4/3] h-auto w-full object-cover" fetchPriority="high" decoding="async" />
      </button>
      <div className="hidden h-[440px] grid-cols-4 grid-rows-2 gap-2 overflow-hidden rounded-[28px] lg:grid xl:h-[500px]">
        {imgs.map((src, i) => (
          <button
            key={src + i}
            type="button"
            onClick={() => onOpen?.(i)}
            className={`group relative block h-full w-full overflow-hidden bg-muted focus-visible:z-10 ${i === 0 ? "col-span-2 row-span-2" : ""}`}
            aria-label={`${alt} ${i + 1}`}
            data-testid={`${testid}-img-${i}`}
          >
            <img {...imgProps(src, i === 0 ? SIZES.half : SIZES.quarter)} alt="" loading={i === 0 ? "eager" : "lazy"} fetchPriority={i === 0 ? "high" : undefined} decoding="async" className="h-full w-full object-cover transition-[filter] duration-200 group-hover:brightness-95" />
          </button>
        ))}
        {imgs.length < 5 && Array.from({ length: 5 - imgs.length }).map((_, k) => <div key={k} className="bg-secondary" />)}
      </div>
      {label && (
        <button type="button" onClick={() => onOpen?.(0)} className="absolute bottom-4 right-4 inline-flex h-9 items-center gap-2 rounded-full bg-white/95 px-3.5 text-sm font-semibold text-foreground shadow-float backdrop-blur transition-colors hover:bg-white" data-testid={`${testid}-open`}>
          <Grip className="h-4 w-4" /> {label}
        </button>
      )}
    </div>
  );
};

/** Tracks which of the given section ids is in view */
export const useActiveSection = (ids = []) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    const io = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.id);
    }, { rootMargin: "-25% 0px -60% 0px", threshold: 0 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [ids.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  return active;
};

/** Pill chips */
export const Chip = ({ active, onClick, children, testid, className = "", as = "button", ...rest }) => {
  const Comp = as;
  return (
    <Comp
      type={as === "button" ? "button" : undefined}
      onClick={onClick}
      aria-pressed={as === "button" ? !!active : undefined}
      data-testid={testid}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${active ? "border-foreground bg-foreground text-background" : "border-border bg-card text-foreground hover:border-foreground/60"} ${className}`}
      {...rest}
    >
      {children}
    </Comp>
  );
};

export const Hairline = ({ className = "" }) => <div className={`h-px w-full bg-border ${className}`} aria-hidden />;

/* ---- compatibility shims (older primitives still referenced by content pages) ---- */
export const Eyebrow = ({ children, className = "", testid }) => <p className={`text-sm font-medium text-muted-foreground ${className}`} data-testid={testid}>{children}</p>;
export const SectionMark = ({ label }) => <p className="text-sm font-medium text-muted-foreground">{label}</p>;
export const SectionHeader = ({ title, intro }) => (<div><h2 className="text-[1.75rem] leading-[1.05] tracking-[-0.03em] sm:text-[2.125rem]">{title}</h2>{intro && <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">{intro}</p>}</div>);
export const InkBand = ({ children, className = "", testid }) => <section className={`bg-primary text-primary-foreground ${className}`} data-testid={testid}><Container className="py-10">{children}</Container></section>;
export const HGallery = HScroll;
export const StickyRail = () => null;
export const PageHero = ({ title, sub, image, testid }) => (<div data-testid={testid}><PageIntro title={title} lead={sub} /><PhotoBanner src={image} /></div>);
export const Stat = ({ value, label, testid }) => (<div data-testid={testid}><p className="text-3xl font-bold tracking-tight tabular">{value}</p><p className="mt-1 text-sm text-muted-foreground">{label}</p></div>);
export const TrustItem = ({ icon: I, title, sub, testid }) => (<div className="flex items-start gap-3" data-testid={testid}><I className="mt-0.5 h-5 w-5 shrink-0 text-foreground" /><div><p className="text-[0.9375rem] font-semibold">{title}</p>{sub && <p className="text-sm text-muted-foreground">{sub}</p>}</div></div>);
